import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { playAlarmBeep } from '../lib/alarmBeep';

const FireOverlay = ({ incident, onDismiss, onAccept, acceptLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stopSoundRef = React.useRef(null);

  const isRinging = incident?.status === 'open';

  React.useEffect(() => {
    if (!incident?.id || !isRinging) return undefined;
    stopSoundRef.current = playAlarmBeep({ frequency: 740, pulseSec: 0.2, intervalMs: 450 });
    return () => {
      if (stopSoundRef.current) {
        stopSoundRef.current();
        stopSoundRef.current = null;
      }
    };
  }, [incident?.id, isRinging]);

  if (!incident || !isRinging) return null;

  const schoolIp = incident.school_ip || incident.schoolIp;
  const deviceId = incident.device_id || incident.primary_device_id;

  const goToSchool = () => {
    if (!schoolIp) return;
    onDismiss?.();
    navigate(`/schools/${encodeURIComponent(schoolIp)}`);
  };

  const goToVideo = () => {
    if (!schoolIp || !deviceId) return;
    onDismiss?.();
    navigate(`/schools/${encodeURIComponent(schoolIp)}/nvr/${deviceId}`);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-3xl border border-orange-500/50 bg-surface-raised p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-orange-400">
              {t('fire.emergency')}
            </p>
            <h2 className="mt-1 text-2xl font-bold text-white">
              {incident.school || t('fire.unknownSchool')}
            </h2>
            <p className="mt-1 text-sm text-muted-300">
              {[incident.nvr || incident.nvr_name, incident.region]
                .filter(Boolean)
                .join(' · ')}
              {incident.io_port_id != null ? ` · IO ${incident.io_port_id}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-full p-2 text-muted-400 hover:bg-surface-subtle hover:text-white"
            aria-label={t('common.close')}
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={acceptLoading}
            onClick={() => onAccept(incident.id)}
            className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-400 disabled:opacity-50"
          >
            <span className="material-icons text-base">check_circle</span>
            {acceptLoading ? t('common.loading') : t('fire.accept')}
          </button>
          <button
            type="button"
            disabled={!schoolIp || !deviceId}
            onClick={goToVideo}
            className="inline-flex items-center gap-2 rounded-2xl border border-surface-border px-5 py-3 text-sm text-muted-200 transition hover:bg-surface-subtle disabled:opacity-40"
          >
            <span className="material-icons text-base">videocam</span>
            {t('fire.goToVideo')}
          </button>
          <button
            type="button"
            disabled={!schoolIp}
            onClick={goToSchool}
            className="inline-flex items-center gap-2 rounded-2xl border border-surface-border px-5 py-3 text-sm text-muted-200 transition hover:bg-surface-subtle disabled:opacity-40"
          >
            <span className="material-icons text-base">school</span>
            {t('fire.goToSchool')}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-2xl border border-surface-border px-5 py-3 text-sm text-muted-200 hover:bg-surface-subtle"
          >
            {t('fire.dismissOverlay')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FireOverlay;
